import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    Chip,
    Paper,
    IconButton,
    Tooltip,
    Alert
} from '@mui/material';
import {
    SmartToy as SmartToyIcon,
    Send as SendIcon,
    Lightbulb as LightbulbIcon,
    Launch as LaunchIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import './AIAssistantDialog.css';
import aiService from '../../services/aiService';

export default function AIAssistantDialog({ open, onClose, flowsheetData = [] }) {
    const navigate = useNavigate();
    const [userInput, setUserInput] = useState('');
    const [chatHistory, setChatHistory] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [aiServiceStatus, setAiServiceStatus] = useState(false);

    // Check AI service status when dialog opens
    useEffect(() => {
        if (open) {
            // Reload API key and check status
            aiService.loadApiKey();
            const isAvailable = aiService.isServiceAvailable();
            setAiServiceStatus(isAvailable);
            
            // Debug logging
            console.log('AI Service Status Check:', {
                isAvailable,
                apiKey: aiService.getApiKey() ? 'Present' : 'Missing',
                lastError: aiService.getLastError()
            });
        }
    }, [open]);

    // Initialize with welcome message
    useEffect(() => {
        if (open && chatHistory.length === 0) {
            const welcomeMessage = `Hello! I'm your Aquaflowz AI Assistant. I can help you with:

• **Flowsheet Selection**: Find the right flowsheet for your needs
• **Parameter Guidance**: Get help with flowsheet parameters  
• **Basic Troubleshooting**: Common WaterTAP issues
• **Navigation**: Go directly to flowsheets

**Try asking me:**
• "What's the cost for 1,000 m³/day RO?"
• "Compare RO vs FO for seawater desalination"
• "Show me GAC flowsheets for organic removal"

**💡 Tip**: Click any flowsheet links in my responses to navigate directly to them!

What would you like help with today?`;

            setChatHistory([
                {
                    type: 'ai',
                    message: welcomeMessage,
                    timestamp: new Date()
                }
            ]);
        }
    }, [open, chatHistory.length, aiServiceStatus]);

    const handleSendMessage = async () => {
        if (!userInput.trim()) return;

        const userMessage = {
            type: 'user',
            message: userInput,
            timestamp: new Date()
        };

        setChatHistory(prev => [...prev, userMessage]);

        setUserInput('');
        setIsLoading(true);

        try {
            if (aiServiceStatus) {
                // Use real AI service
                const aiResponse = await aiService.generateAIResponse(userInput, chatHistory);
                setChatHistory(prev => [...prev, aiResponse]);
            } else {
                // Show specific error message instead of fallback
                const errorResponse = {
                    type: 'ai',
                    message: `❌ AI Service Unavailable\n\n${aiService.getLastError() || 'Please configure your OpenAI API key in the .env file or through the UI settings.'}\n\nTo fix this:\n1. Add your OpenAI API key to the .env file in the root directory\n2. Set REACT_APP_OPENAI_API_KEY=sk-your-actual-key-here\n3. Restart the application`,
                    timestamp: new Date()
                };
                setChatHistory(prev => [...prev, errorResponse]);
            }
        } catch (error) {
            console.error('Error generating AI response:', error);
            const errorResponse = {
                type: 'ai',
                message: `❌ API Error\n\n${error.message}\n\nPlease check your API key configuration and try again.`,
                timestamp: new Date()
            };
            setChatHistory(prev => [...prev, errorResponse]);
        } finally {
            setIsLoading(false);
        }
    };

    // Find flowsheet by name and navigate to it
    const findAndNavigateToFlowsheet = (flowsheetName) => {
        // Clean up the flowsheet name to match the data
        const cleanName = flowsheetName.toLowerCase().replace(/\s+/g, ' ').trim();
        
        // Check if it's a custom flowsheet with direct URL mapping
        const customFlowsheetUrls = {
            'microfiltration flowsheet': '#/flowsheet/microfiltration_ui/config',
            'pre-filtration flowsheet': '#/flowsheet/prefiltration_fixed_ui/config',
            'prefiltration flowsheet': '#/flowsheet/prefiltration_fixed_ui/config',
            'uv filtration flowsheet': '#/flowsheet/UV_filtration_ui/config',
            'uv flowsheet': '#/flowsheet/UV_filtration_ui/config'
        };
        
        const customUrl = customFlowsheetUrls[cleanName];
        if (customUrl) {
            navigate(customUrl, { replace: true });
            onClose();
            return {
                type: 'ai',
                message: `🎯 Perfect! I've navigated you to "${flowsheetName}". You can now configure and run this flowsheet.`,
                timestamp: new Date()
            };
        }
        
        // Search through available flowsheets
        const foundFlowsheet = flowsheetData.find(flowsheet => {
            const flowsheetDesc = flowsheet.description?.toLowerCase() || '';
            const flowsheetName_lower = flowsheet.name?.toLowerCase() || '';
            
            return flowsheetDesc.includes(cleanName) || 
                   flowsheetName_lower.includes(cleanName) ||
                   cleanName.includes(flowsheetDesc) ||
                   cleanName.includes(flowsheetName_lower);
        });

        if (foundFlowsheet) {
            // Set current flowsheet for parameter auto-fill

            
            // Navigate to the flowsheet
            const hasOptions = Object.keys(foundFlowsheet.build_options || {}).length > 1;
            navigate(`/flowsheet/${foundFlowsheet.id_}/config`, {
                replace: true,
                state: { built: foundFlowsheet.built, hasOptions: hasOptions }
            });
            
            // Close the AI Assistant dialog
            onClose();
            
            return {
                type: 'ai',
                message: `🎯 Perfect! I've navigated you to "${foundFlowsheet.description}". You can now configure and run this flowsheet.`,
                timestamp: new Date()
            };
        } else {
            // Flowsheet not found
            return {
                type: 'ai',
                message: `❌ I couldn't find the exact flowsheet "${flowsheetName}" in your available flowsheets. 

Available flowsheets:
${flowsheetData.map(fs => `• ${fs.description || fs.name}`).join('\n')}

Would you like me to recommend a different approach or help you find a similar flowsheet?`,
                timestamp: new Date()
            };
        }
    };

    const handleFlowsheetSelect = (flowsheetName) => {
        const navigationResult = findAndNavigateToFlowsheet(flowsheetName);
        
        if (navigationResult.message.includes('🎯')) {
            // Success - add success message and close
            setChatHistory(prev => [...prev, navigationResult]);
        } else {
            // Not found - add error message
            setChatHistory(prev => [...prev, navigationResult]);
        }
    };





    const handleKeyPress = (event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handleSendMessage();
        }
    };




    return (
        <>
            <Dialog 
                open={open} 
                onClose={onClose}
                maxWidth="md"
                fullWidth
                className="ai-assistant-dialog"
                PaperProps={{
                    sx: { height: '80vh' }
                }}
            >
                <DialogTitle sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    backgroundColor: 'primary.main',
                    color: 'white'
                }}>
                    <SmartToyIcon />
                    AI Water Treatment Assistant
                    <Box sx={{ flexGrow: 1 }} />
                    
                </DialogTitle>

                <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>

                    {/* Chat History */}
                    <Box sx={{ 
                        flexGrow: 1, 
                        overflowY: 'auto', 
                        p: 2,
                        backgroundColor: '#f5f5f5'
                    }}
                    className="chat-history"
                    >
                        {chatHistory.map((chat, index) => (
                            <Box key={index} sx={{ mb: 2 }}>
                                <Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'flex-start', 
                                    gap: 1,
                                    justifyContent: chat.type === 'user' ? 'flex-end' : 'flex-start'
                                }}>
                                    {chat.type === 'ai' && (
                                        <SmartToyIcon sx={{ color: 'primary.main', mt: 0.5 }} />
                                    )}
                                    
                                    <Paper sx={{ 
                                        p: 2, 
                                        maxWidth: '80%',
                                        backgroundColor: chat.type === 'user' ? 'primary.main' : 'white',
                                        color: chat.type === 'user' ? 'white' : 'text.primary'
                                    }}>
                                        <Box sx={{ 
                                            '& a': { 
                                                color: 'primary.main', 
                                                textDecoration: 'underline',
                                                cursor: 'pointer',
                                                '&:hover': { 
                                                    color: 'primary.dark',
                                                    textDecoration: 'none'
                                                }
                                            }
                                        }}>
                                            <ReactMarkdown 
                                                components={{
                                                    a: ({ href, children }) => {
                                                        if (href && href.startsWith('/flowsheets/')) {
                                                            const flowsheetId = href.split('/flowsheets/')[1];
                                                            return (
                                                                <a 
                                                                    href={href}
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        navigate(`/flowsheets/${flowsheetId}`);
                                                                        onClose();
                                                                    }}
                                                                >
                                                                    {children}
                                                                </a>
                                                            );
                                                        }
                                                        return <a href={href}>{children}</a>;
                                                    }
                                                }}
                                            >
                                                {chat.message}
                                            </ReactMarkdown>
                                        </Box>
                                        
                                        {/* Show recommendations if available */}
                                        {chat.recommendations && chat.recommendations.length > 0 && (
                                            <Box sx={{ mt: 2 }}>
                                                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                                    <LightbulbIcon sx={{ mr: 1, fontSize: 'small' }} />
                                                    Recommended Flowsheets:
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                    {chat.recommendations.map((flowsheet, idx) => (
                                                        <Chip
                                                            key={idx}
                                                            label={flowsheet}
                                                            onClick={() => handleFlowsheetSelect(flowsheet)}
                                                            sx={{ 
                                                                cursor: 'pointer',
                                                                '&:hover': { backgroundColor: 'primary.light' }
                                                            }}
                                                            color="primary"
                                                            variant="outlined"
                                                            icon={<LaunchIcon />}
                                                        />
                                                    ))}
                                                </Box>
                                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                                    💡 Click any flowsheet to navigate to it directly!
                                                </Typography>
                                                
                                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                                    💡 Click on any flowsheet above to navigate to it directly!
                                                </Typography>
                                            </Box>
                                        )}

                                        {/* Show explanation if available */}
                                        {chat.explanation && (
                                            <Box sx={{ mt: 2, p: 1, backgroundColor: 'info.light', borderRadius: 1 }}>
                                                <Typography variant="body2" color="info.dark">
                                                    💡 {chat.explanation}
                                                </Typography>
                                            </Box>
                                        )}

                                        {/* Show parameter suggestions if available */}
                                        {chat.parameters && Object.keys(chat.parameters).length > 0 && (
                                            <Box sx={{ mt: 2 }}>
                                                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                                    <LightbulbIcon sx={{ mr: 1, fontSize: 'small' }} />
                                                    Suggested Parameters:
                                                </Typography>
                                                {Object.entries(chat.parameters).map(([flowsheetName, params]) => (
                                                    <Box key={flowsheetName} sx={{ mb: 1 }}>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {flowsheetName}:
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                                                            {Object.entries(params).map(([paramName, values]) => (
                                                                <Chip
                                                                    key={paramName}
                                                                    label={`${paramName}: ${values.optimal} ${values.unit}`}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    sx={{ fontSize: '0.7rem' }}
                                                                />
                                                            ))}
                                                        </Box>
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}

                                        {/* Show additional insights if available */}
                                        {chat.additionalInsights && (
                                            <Box sx={{ mt: 2, p: 1, backgroundColor: 'success.light', borderRadius: 1 }}>
                                                <Typography variant="body2" color="success.dark">
                                                    💡 {chat.additionalInsights}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Paper>
                                </Box>
                            </Box>
                        ))}
                        
                        {isLoading && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
                                <SmartToyIcon sx={{ color: 'primary.main' }} />
                                <Typography variant="body2" color="text.secondary">
                                    AI is thinking...
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {/* Input Area */}
                    <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }} className="chat-input">
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField
                                fullWidth
                                multiline
                                maxRows={3}
                                value={userInput}
                                onChange={(e) => setUserInput(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder="Describe your water treatment needs... (e.g., 'I need to remove salt from seawater')"
                                variant="outlined"
                                size="small"
                            />
                            <Button
                                variant="contained"
                                onClick={handleSendMessage}
                                disabled={!userInput.trim() || isLoading}
                                sx={{ minWidth: 'auto', px: 2 }}
                            >
                                <SendIcon />
                            </Button>
                        </Box>
                        
                        {/* Quick Suggestions */}
                        {aiServiceStatus && (
                            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                                    Quick suggestions:
                                </Typography>
                                {['remove salt from seawater', 'energy efficient desalination', 'remove organic contaminants'].map((suggestion) => (
                                    <Chip
                                        key={suggestion}
                                        label={suggestion}
                                        size="small"
                                        onClick={() => setUserInput(suggestion)}
                                        sx={{ cursor: 'pointer', fontSize: '0.7rem' }}
                                        variant="outlined"
                                    />
                                ))}
                            </Box>
                        )}
                    </Box>
                </DialogContent>

                <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                    <Button onClick={onClose} color="inherit">
                        Close
                    </Button>
                    <Button 
                        onClick={() => setChatHistory([])} 
                        color="secondary"
                        variant="outlined"
                    >
                        Clear Chat
                    </Button>
                </DialogActions>
            </Dialog>



        </>
    );
}
